import axios from 'axios';

// Simple API service for Constant Love app
class ApiService {
  constructor() {
    const baseURL = '/api';
    
    this.axios = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Add auth token interceptor
    this.axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    console.log('API Service initialized with baseURL:', baseURL);
  }

  // Get authenticated user ID from token
  getUserId() {
    const token = localStorage.getItem('auth_token');
    if (!token) return null;
    
    try {
      const payload = JSON.parse(window.atob(token.split('.')[1]));
      return payload.user_id;
    } catch {
      return null;
    }
  }

  // Generate affirmation
  async generateAffirmation(mood) {
    try {
      const userId = this.getUserId();
      console.log('🎯 Starting affirmation generation:', { mood, userId });
      
      const requestData = {
        mood: parseInt(mood),
        userId: userId
      };
      
      console.log('📤 Sending request:', requestData);
      
      const response = await this.axios.post('/affirmations/generate', requestData);
      
      console.log('📦 Raw response:', response);
      console.log('✅ Response data:', response.data);
      console.log('📊 Response status:', response.status);
      
      // Validate response format
      if (!response.data || !response.data.affirmation) {
        console.error('❌ Invalid response format:', response.data);
        throw new Error('Invalid response from server');
      }
      
      console.log('🎉 Affirmation generation successful!');
      return response.data;
      
    } catch (error) {
      console.error('💥 DETAILED ERROR INFO:');
      console.error('Error object:', error);
      console.error('Error message:', error.message);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      console.error('Request config:', error.config);
      
      if (error.response) {
        throw new Error(`Server error: ${error.response.status} - ${error.response.data?.detail || error.message}`);
      } else if (error.request) {
        throw new Error('Network error: Unable to reach server');
      } else {
        throw new Error(`Request error: ${error.message}`);
      }
    }
  }

  // Save affirmation
  async saveAffirmation(affirmationId) {
    try {
      const userId = this.getUserId();
      console.log('Saving affirmation:', affirmationId, 'for user:', userId);
      
      const response = await this.axios.post('/affirmations/save', {
        affirmationId: affirmationId,
        userId: userId
      });

      console.log('Affirmation saved successfully');
      return response.data;
    } catch (error) {
      console.error('Failed to save affirmation:', error.response?.data || error.message);
      throw new Error('Failed to save affirmation.');
    }
  }

  // Get saved affirmations
  async getSavedAffirmations() {
    try {
      const userId = this.getUserId();
      console.log('Getting saved affirmations for user:', userId);
      
      const response = await this.axios.get(`/affirmations/saved/${userId}`);
      
      console.log('Retrieved saved affirmations:', response.data);
      return response.data.affirmations || [];
    } catch (error) {
      console.error('Failed to get saved affirmations:', error.response?.data || error.message);
      return [];
    }
  }

  // Unsave affirmation
  async unsaveAffirmation(affirmationId) {
    try {
      const userId = this.getUserId();
      console.log('Unsaving affirmation:', affirmationId, 'for user:', userId);
      
      const response = await this.axios.delete('/affirmations/unsave', {
        data: {
          affirmationId: affirmationId,
          userId: userId
        }
      });

      console.log('Affirmation unsaved successfully');
      return response.data;
    } catch (error) {
      console.error('Failed to unsave affirmation:', error.response?.data || error.message);
      throw new Error('Failed to unsave affirmation.');
    }
  }

  // Check-in streak
  async checkinStreak() {
    try {
      const userId = this.getUserId();
      console.log('Checking in streak for user:', userId);
      
      const response = await this.axios.post('/streaks/checkin', {
        userId: userId
      });

      console.log('Streak check-in successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to check-in streak:', error.response?.data || error.message);
      throw new Error('Failed to update streak.');
    }
  }

  // Get streak data
  async getStreakData() {
    try {
      const userId = this.getUserId();
      console.log('Getting streak data for user:', userId);
      
      const response = await this.axios.get(`/streaks/${userId}`);
      
      console.log('Retrieved streak data:', response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to get streak data:', error.response?.data || error.message);
      return { currentStreak: 0, bestStreak: 0, totalCheckins: 0 };
    }
  }

  // Test API connection
  async testConnection() {
    try {
      console.log('Testing API connection...');
      const response = await this.axios.get('/');
      console.log('API connection successful:', response.data);
      return true;
    } catch (error) {
      console.error('API connection failed:', error.response?.data || error.message);
      return false;
    }
  }
}

// Create and export singleton
const apiService = new ApiService();
export default apiService;

// Also export getUserId for compatibility
export const getUserId = () => apiService.getUserId();